import React, { Suspense } from 'react';
import PageSkeleton from '@/modules/ui/skeleton/PageSkeleton';
import { Navigate, RouteObject } from 'react-router';
import { EntityLayout } from '@/modules/ui/layouts/EntityLayout';

const DashboardPage = React.lazy(() => import('@/modules/dashboard/home/DashboardPage'));
const CustomersPage = React.lazy(() => import('@/modules/customers/pages/CustomersPage'));
const NewCustomerPage = React.lazy(() => import('@/modules/customers/pages/NewCustomerPage'));
const EditCustomerPage = React.lazy(() => import('@/modules/customers/pages/EditCustomerPage'));
const ProductsPage = React.lazy(() => import('@/modules/products/pages/ProductsPage'));
const NewProductPage = React.lazy(() => import('@/modules/products/pages/NewProductPage'));
const EditProductPage = React.lazy(() => import('@/modules/products/pages/EditProductPage'));
const ServicesPage = React.lazy(() => import('@/modules/services/pages/ServicesPage'));
const NewServicePage = React.lazy(() => import('@/modules/services/pages/NewServicePage'));
const EditServicePage = React.lazy(() => import('@/modules/services/pages/EditServicePage'));
const UsersPage = React.lazy(() => import('@/modules/users/pages/UsersPage'));
const NewUserPage = React.lazy(() => import('@/modules/users/pages/NewUserPage'));
const EditUserPage = React.lazy(() => import('@/modules/users/pages/EditUserPage'));
const SuppliersListPage = React.lazy(() => import('@/modules/suppliers/pages/SuppliersListPage'));
const NewSupplierPage = React.lazy(() => import('@/modules/suppliers/pages/NewSupplierPage'));
const SupplierDetailPage = React.lazy(() => import('@/modules/suppliers/pages/SupplierDetailPage'));
const SettingsPage = React.lazy(() => import('@/modules/settings/pages/SettingsPage'));
const AttendancePage = React.lazy(
  () => import('@/modules/operations/attendance/pages/AttendancePage'),
);
const PurchaseInvoicesListPage = React.lazy(
  () => import('@/modules/purchase-invoices/pages/PurchaseInvoicesListPage'),
);
const NewPurchaseInvoicePage = React.lazy(
  () => import('@/modules/purchase-invoices/pages/NewPurchaseInvoicePage'),
);
const PurchaseInvoiceDetailPage = React.lazy(
  () => import('@/modules/purchase-invoices/pages/PurchaseInvoiceDetailPage'),
);
const QuotesPage = React.lazy(() => import('@/modules/billing/quotes/pages/QuotesPage'));
const NewQuotePage = React.lazy(() => import('@/modules/billing/quotes/pages/NewQuotePage'));
const EditQuotePage = React.lazy(() => import('@/modules/billing/quotes/pages/EditQuotePage'));
const InvoicesPage = React.lazy(() => import('@/modules/billing/invoices/pages/InvoicesPage'));
const NewInvoicePage = React.lazy(() => import('@/modules/billing/invoices/pages/NewInvoicePage'));
const EditInvoicePage = React.lazy(
  () => import('@/modules/billing/invoices/pages/EditInvoicePage'),
);
const DeliveryNotesPage = React.lazy(
  () => import('@/modules/delivery-notes/pages/DeliveryNotesPage'),
);
const EditDeliveryNotePage = React.lazy(
  () => import('@/modules/delivery-notes/pages/EditDeliveryNotePage'),
);
const NoticesPage = React.lazy(() => import('../../modules/operations/notices/pages/NoticePage'));
const NewNoticePage = React.lazy(() => import('@/modules/operations/notices/pages/NewNoticePage'));
const EditNoticePage = React.lazy(
  () => import('@/modules/operations/notices/pages/EditNoticePage'),
);
const IncidencesPage = React.lazy(
  () => import('@/modules/operations/incidences/pages/IncidencesPage'),
);
const NewIncidencePage = React.lazy(
  () => import('@/modules/operations/incidences/pages/NewIncidencePage'),
);
const EditIncidencePage = React.lazy(
  () => import('@/modules/operations/incidences/pages/EditIncidencePage'),
);
const IncidencesCalendarPage = React.lazy(
  () => import('@/modules/operations/calendar/pages/IncidencesCalendarPage'),
);
const WorkOrdersPage = React.lazy(() => import('@/modules/work-orders/pages/WorkOrdersPage'));
const NewWorkOrderPage = React.lazy(() => import('@/modules/work-orders/pages/NewWorkOrderPage'));
const EditWorkOrderPage = React.lazy(() => import('@/modules/work-orders/pages/EditWorkOrderPage'));
const StockPage = React.lazy(() => import('@/modules/stock/pages/StockPage'));
const ConsolidatedStockPage = React.lazy(
  () => import('@/modules/stock/pages/ConsolidatedStockPage'),
);
const SerialNumbersPage = React.lazy(() => import('@/modules/stock/pages/SerialNumbersPage'));
const AccountsPayablePage = React.lazy(
  () => import('@/modules/accounts-payable/pages/AccountsPayableDashboardPage'),
);
const AccountingPage = React.lazy(() => import('@/modules/accounting/pages/AccountingPage'));
const BrandSettingsPage = React.lazy(
  () => import('../../modules/settings/brand/pages/BrandSettingsPage'),
);
const CertificateSealSettingsPage = React.lazy(
  () => import('@/modules/settings/certificate-seal/pages/CertificateSealSettingsPage'),
);
const UserSettingsPage = React.lazy(() => import('@/modules/settings/pages/UserSettingsPage'));
const CatalogsSettingsPage = React.lazy(
  () => import('@/modules/settings/catalogs/pages/CatalogsSettingsPage'),
);
const NotificationsSettingsPage = React.lazy(
  () => import('@/modules/settings/pages/NotificationsSettingsPage'),
);

export const dashboardChildren: RouteObject[] = [
  { index: true, element: <Navigate to="/dashboard" replace /> },
  {
    path: 'dashboard',
    element: (
      <Suspense fallback={<PageSkeleton />}>
        <DashboardPage />
      </Suspense>
    ),
    handle: { labelKey: 'Navigation.dashboard' },
  },
  {
    path: 'customers',
    element: <EntityLayout label="Clientes" href="/customers" />,
    children: [
      {
        index: true,
        element: (
          <Suspense fallback={<PageSkeleton />}>
            <CustomersPage />
          </Suspense>
        ),
        handle: { labelKey: 'Navigation.customers' },
      },
      {
        path: 'new',
        element: (
          <Suspense fallback={<PageSkeleton />}>
            <NewCustomerPage />
          </Suspense>
        ),
      },
      {
        path: ':id',
        element: (
          <Suspense fallback={<PageSkeleton />}>
            <EditCustomerPage />
          </Suspense>
        ),
      },
    ],
  },
  {
    path: 'products',
    element: <EntityLayout label="Productos" href="/products" />,
    children: [
      {
        index: true,
        element: (
          <Suspense fallback={<PageSkeleton />}>
            <ProductsPage />
          </Suspense>
        ),
        handle: { labelKey: 'Navigation.products' },
      },
      {
        path: 'new',
        element: (
          <Suspense fallback={<PageSkeleton />}>
            <NewProductPage />
          </Suspense>
        ),
      },
      {
        path: ':id',
        element: (
          <Suspense fallback={<PageSkeleton />}>
            <EditProductPage />
          </Suspense>
        ),
      },
    ],
  },
  {
    path: 'services',
    element: <EntityLayout label="Servicios" href="/services" />,
    children: [
      {
        index: true,
        element: (
          <Suspense fallback={<PageSkeleton />}>
            <ServicesPage />
          </Suspense>
        ),
        handle: { labelKey: 'Navigation.services' },
      },
      {
        path: 'new',
        element: (
          <Suspense fallback={<PageSkeleton />}>
            <NewServicePage />
          </Suspense>
        ),
      },
      {
        path: ':id',
        element: (
          <Suspense fallback={<PageSkeleton />}>
            <EditServicePage />
          </Suspense>
        ),
      },
    ],
  },
  {
    path: 'users',
    element: <EntityLayout label="Usuarios" href="/users" />,
    children: [
      {
        index: true,
        element: (
          <Suspense fallback={<PageSkeleton />}>
            <UsersPage />
          </Suspense>
        ),
        handle: { labelKey: 'Navigation.users' },
      },
      {
        path: 'new',
        element: (
          <Suspense fallback={<PageSkeleton />}>
            <NewUserPage />
          </Suspense>
        ),
      },
      {
        path: ':id',
        element: (
          <Suspense fallback={<PageSkeleton />}>
            <EditUserPage />
          </Suspense>
        ),
      },
    ],
  },
  {
    path: 'suppliers',
    element: <EntityLayout label="Proveedores" href="/suppliers" />,
    children: [
      {
        index: true,
        element: (
          <Suspense fallback={<PageSkeleton />}>
            <SuppliersListPage />
          </Suspense>
        ),
        handle: { labelKey: 'Navigation.suppliers' },
      },
      {
        path: 'new',
        element: (
          <Suspense fallback={<PageSkeleton />}>
            <NewSupplierPage />
          </Suspense>
        ),
      },
      {
        path: ':id',
        element: (
          <Suspense fallback={<PageSkeleton />}>
            <SupplierDetailPage />
          </Suspense>
        ),
      },
    ],
  },
  {
    path: 'purchase-orders',
    children: [
      { index: true, element: <Navigate to="/purchase-invoices" replace /> },
      { path: 'new', element: <Navigate to="/purchase-invoices" replace /> },
      { path: ':id', element: <Navigate to="/purchase-invoices" replace /> },
    ],
  },
  {
    path: 'purchase-invoices',
    element: <EntityLayout label="Facturas de Compra" href="/purchase-invoices" />,
    children: [
      {
        index: true,
        element: (
          <Suspense fallback={<PageSkeleton />}>
            <PurchaseInvoicesListPage />
          </Suspense>
        ),
        handle: { labelKey: 'Navigation.purchaseInvoices' },
      },
      {
        path: 'new',
        element: (
          <Suspense fallback={<PageSkeleton />}>
            <NewPurchaseInvoicePage />
          </Suspense>
        ),
      },
      {
        path: ':id',
        element: (
          <Suspense fallback={<PageSkeleton />}>
            <PurchaseInvoiceDetailPage />
          </Suspense>
        ),
      },
    ],
  },
  {
    path: 'billing/quotes',
    element: <EntityLayout label="Presupuestos" href="/billing/quotes" />,
    children: [
      {
        index: true,
        element: (
          <Suspense fallback={<PageSkeleton />}>
            <QuotesPage />
          </Suspense>
        ),
        handle: { labelKey: 'Navigation.quotes' },
      },
      {
        path: 'new',
        element: (
          <Suspense fallback={<PageSkeleton />}>
            <NewQuotePage />
          </Suspense>
        ),
      },
      {
        path: ':id',
        element: (
          <Suspense fallback={<PageSkeleton />}>
            <EditQuotePage />
          </Suspense>
        ),
      },
    ],
  },
  {
    path: 'billing/invoices',
    element: <EntityLayout label="Facturas" href="/billing/invoices" />,
    children: [
      {
        index: true,
        element: (
          <Suspense fallback={<PageSkeleton />}>
            <InvoicesPage />
          </Suspense>
        ),
        handle: { labelKey: 'Navigation.invoices' },
      },
      {
        path: 'new',
        element: (
          <Suspense fallback={<PageSkeleton />}>
            <NewInvoicePage />
          </Suspense>
        ),
      },
      {
        path: ':id',
        element: (
          <Suspense fallback={<PageSkeleton />}>
            <EditInvoicePage />
          </Suspense>
        ),
      },
    ],
  },
  {
    path: 'billing/delivery-notes',
    element: <EntityLayout label="Albaranes" href="/billing/delivery-notes" />,
    children: [
      {
        index: true,
        element: (
          <Suspense fallback={<PageSkeleton />}>
            <DeliveryNotesPage />
          </Suspense>
        ),
        handle: { labelKey: 'Navigation.deliveryNotes' },
      },
      {
        path: ':id',
        element: (
          <Suspense fallback={<PageSkeleton />}>
            <EditDeliveryNotePage />
          </Suspense>
        ),
      },
    ],
  },
  {
    path: 'operations/notices',
    element: <EntityLayout label="Avisos" href="/operations/notices" />,
    children: [
      {
        index: true,
        element: (
          <Suspense fallback={<PageSkeleton />}>
            <NoticesPage />
          </Suspense>
        ),
        handle: { labelKey: 'Navigation.notices' },
      },
      {
        path: 'new',
        element: (
          <Suspense fallback={<PageSkeleton />}>
            <NewNoticePage />
          </Suspense>
        ),
      },
      {
        path: ':id',
        element: (
          <Suspense fallback={<PageSkeleton />}>
            <EditNoticePage />
          </Suspense>
        ),
      },
    ],
  },
  {
    path: 'operations/incidences',
    element: <EntityLayout label="Incidencias" href="/operations/incidences" />,
    children: [
      {
        index: true,
        element: (
          <Suspense fallback={<PageSkeleton />}>
            <IncidencesPage />
          </Suspense>
        ),
        handle: { labelKey: 'Navigation.incidences' },
      },
      {
        path: 'new',
        element: (
          <Suspense fallback={<PageSkeleton />}>
            <NewIncidencePage />
          </Suspense>
        ),
      },
      {
        path: ':id',
        element: (
          <Suspense fallback={<PageSkeleton />}>
            <EditIncidencePage />
          </Suspense>
        ),
      },
    ],
  },
  {
    path: 'operations/calendar',
    element: <EntityLayout label="Calendario" href="/operations/calendar" />,
    children: [
      {
        index: true,
        element: (
          <Suspense fallback={<PageSkeleton />}>
            <IncidencesCalendarPage />
          </Suspense>
        ),
        handle: { labelKey: 'Navigation.calendar' },
      },
    ],
  },
  {
    path: 'operations/work-orders',
    element: <EntityLayout label="Ordenes de Trabajo" href="/operations/work-orders" />,
    children: [
      {
        index: true,
        element: (
          <Suspense fallback={<PageSkeleton />}>
            <WorkOrdersPage />
          </Suspense>
        ),
        handle: { labelKey: 'Navigation.workOrders' },
      },
      {
        path: 'new',
        element: (
          <Suspense fallback={<PageSkeleton />}>
            <NewWorkOrderPage />
          </Suspense>
        ),
      },
      {
        path: ':id',
        element: (
          <Suspense fallback={<PageSkeleton />}>
            <EditWorkOrderPage />
          </Suspense>
        ),
      },
    ],
  },
  {
    path: 'operations/attendance',
    element: <EntityLayout label="Asistencia" href="/operations/attendance" />,
    children: [
      {
        index: true,
        element: (
          <Suspense fallback={<PageSkeleton />}>
            <AttendancePage />
          </Suspense>
        ),
        handle: { labelKey: 'Navigation.attendance' },
      },
    ],
  },
  {
    path: 'stock',
    element: <EntityLayout label="Stock" href="/stock" />,
    children: [
      {
        index: true,
        element: (
          <Suspense fallback={<PageSkeleton />}>
            <StockPage />
          </Suspense>
        ),
        handle: { labelKey: 'Navigation.stock' },
      },
      {
        path: 'consolidated',
        element: (
          <Suspense fallback={<PageSkeleton />}>
            <ConsolidatedStockPage />
          </Suspense>
        ),
      },
      {
        path: 'serial-numbers',
        element: (
          <Suspense fallback={<PageSkeleton />}>
            <SerialNumbersPage />
          </Suspense>
        ),
      },
    ],
  },
  {
    path: 'accounts-payable',
    element: <EntityLayout label="Cuentas por Pagar" href="/accounts-payable" />,
    children: [
      {
        index: true,
        element: (
          <Suspense fallback={<PageSkeleton />}>
            <AccountsPayablePage />
          </Suspense>
        ),
        handle: { labelKey: 'Navigation.accountsPayable' },
      },
    ],
  },
  {
    path: 'accounting',
    element: <EntityLayout label="Contabilidad" href="/accounting" />,
    children: [
      {
        index: true,
        element: (
          <Suspense fallback={<PageSkeleton />}>
            <AccountingPage />
          </Suspense>
        ),
        handle: { labelKey: 'Navigation.accounting' },
      },
    ],
  },
  {
    path: 'settings',
    element: <SettingsPage />,
    children: [
      {
        path: 'brand',
        element: (
          <Suspense fallback={<PageSkeleton />}>
            <BrandSettingsPage />
          </Suspense>
        ),
        handle: { labelKey: 'Navigation.brandSettings' },
      },
      {
        path: 'certificate-seal',
        element: (
          <Suspense fallback={<PageSkeleton />}>
            <CertificateSealSettingsPage />
          </Suspense>
        ),
        handle: { labelKey: 'Navigation.certificateSealSettings' },
      },
      {
        path: 'user',
        element: (
          <Suspense fallback={<PageSkeleton />}>
            <UserSettingsPage />
          </Suspense>
        ),
        handle: { labelKey: 'Navigation.userSettings' },
      },
      {
        path: 'catalogs',
        element: (
          <Suspense fallback={<PageSkeleton />}>
            <CatalogsSettingsPage />
          </Suspense>
        ),
        handle: { labelKey: 'Navigation.catalogs' },
      },
      {
        path: 'notifications',
        element: (
          <Suspense fallback={<PageSkeleton />}>
            <NotificationsSettingsPage />
          </Suspense>
        ),
        handle: { labelKey: 'Navigation.notifications' },
      },
    ],
  },
];
